import type { CSSProperties, ReactElement } from "react";
import { space } from "@tokens/index";
import { Button } from "@primitives/Button";
import { Text } from "@primitives/Text";

/**
 * ComposeFooter: a quiet recipient summary on the left, Cancel and Send on
 * the right. The dialog card is the form (`Dialog as="form"`), so Send is a
 * plain submit button.
 */

export interface ComposeFooterProps {
  readonly summary: string;
  readonly canSend: boolean;
  readonly isSubmitting: boolean;
  readonly compact: boolean;
  readonly onCancel: () => void;
}

const SEND_SHORTCUT = "Control+Enter Meta+Enter";

export function ComposeFooter({
  summary,
  canSend,
  isSubmitting,
  compact,
  onCancel,
}: ComposeFooterProps): ReactElement {
  const rootStyle: CSSProperties = compact
    ? { display: "grid", gap: space[3], width: "100%" }
    : { display: "flex", alignItems: "center", gap: space[3], width: "100%" };
  const actionsStyle: CSSProperties = compact
    ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: space[2] }
    : { display: "flex", gap: space[2], marginLeft: "auto" };

  return (
    <div style={rootStyle}>
      <span aria-live="polite" style={{ flex: 1, minWidth: 0, minHeight: "1.2em" }}>
        <Text as="span" role="caption" tone="muted" truncate style={{ display: "block" }}>
          {summary}
        </Text>
      </span>
      <div style={actionsStyle}>
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={onCancel}
          disabled={isSubmitting}
          fullWidth={compact}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="md"
          busy={isSubmitting}
          disabled={!canSend && !isSubmitting}
          fullWidth={compact}
          title="Ctrl+Enter"
          aria-keyshortcuts={SEND_SHORTCUT}
        >
          Send
        </Button>
      </div>
    </div>
  );
}
